import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './AdCards.styles';
import { AdCardsProps } from './AdCards.types';
import { AdCard } from '@/components/AdCard/AdCard';
import { generateRandomNumber, getDataStatAttr } from '@/utils/helpers';
import { Popup } from '@/components/UI/Popup/Popup';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Ad } from '@/api/types/common.types';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import {
  CollectionMasterModal,
  CollectionMasterSheet,
} from '@/features/BookmarkCollections/components';
import { useToastStore } from '@/store/useToastStore';
import { AdCardNew } from '@/components/AdCardNew';
import NoSsr from '@/components/NoSsr/NoSsr';
import { useResponsive } from '@/hooks/useResponsive';
import { useBannerPosition } from '@/components/AdCards/AdCards.hooks';
import { useViewedAds } from '@/hooks/useViewedAds';
import { useIosNudgeScrollOffTop } from '@/hooks/useIosNudgeScrollOffTop';
import { usePhoneNumberHighlightFilter } from '@/features/PhoneNumberSearch';

export const AdCards = React.memo(
  ({
    ads,
    listDataStat,
    hasActions,
    hasFavorite,
    isInitialLoading,
    chunkSize = AdsChunkSize.Small,
    isLoadingMore,
    bannerInList,
    inListDesktopBanner,
    bottomBanner,
    isBannerInListAtTheEnd,
    isBannerInListAfterThreeRows,
    hasFirstCardPriority,
    sectionIndex,
    adTransitionSource,
    className,
  }: AdCardsProps) => {
    const { t } = useTranslation();
    const { isDesktop } = useResponsive();
    const [isPopupOpen, popupToggle] = useToggleOpenState();
    const [selectedAdId, setSelectedAdId] = useState('');
    const { openToast, closeToast, onAnimationComplete } = useToastStore();
    const getBannerPosition = useBannerPosition({
      isDesktop,
      sectionIndex,
      bannerInList,
      inListDesktopBanner,
      isBannerInListAfterThreeRows,
    });
    const [isCollectionMasterOpen, setIsCollectionMasterOpen] = useState(false);
    const { canShowViewedState, viewedIds } = useViewedAds();
    const iosNudgeScrollOffTop = useIosNudgeScrollOffTop();
    const phoneNumberFilter = usePhoneNumberHighlightFilter();

    const handleAddToFavorites = (targetAd: Ad) => {
      closeToast();
      onAnimationComplete();

      setTimeout(() => {
        openToast({
          image: targetAd?.photo?.url,
          text: t('bookmark_collections.toast_tip'),
          action: t('bookmark_collections.toast_action'),
          onActionClick: handleToastActionClick,
        });
      }, 50);
    };

    const handleRemoveFromFavorites = (targetAd: Ad) => {
      closeToast();
      onAnimationComplete();

      setTimeout(() => {
        openToast({
          image: targetAd?.photo?.url,
          text: t('bookmark_collections.unbookmarked'),
        });
      }, 50);
    };

    const handleToastActionClick = () => {
      closeToast();
      iosNudgeScrollOffTop();
      setIsCollectionMasterOpen(true);
    };

    const onTriggerRemove = useCallback(
      (id: Ad['id']) => {
        setSelectedAdId(id);
        popupToggle();
      },
      [popupToggle]
    );

    const remove = useCallback(() => {
      setSelectedAdId('');
      popupToggle();
    }, [selectedAdId, popupToggle]);

    const dataStatAttr = useMemo(
      () => getDataStatAttr({ dataStat: listDataStat }),
      [listDataStat]
    );

    const popupActions = useMemo(
      () => ({
        left: { text: t('common.decline'), onClick: popupToggle },
        right: { text: t('common.remove'), isRed: true, onClick: remove },
      }),
      [t, popupToggle, remove]
    );

    const renderedAds = useMemo(() => {
      return ads?.map((item, idx) => {
        const { id } = item;
        const key = `${id}_${idx}`;
        const adCard = (
          <AdCardNew
            key={key}
            ad={item}
            hasBookmarkButton={hasFavorite}
            onAddBookmark={handleAddToFavorites}
            onRemoveBookmark={handleRemoveFromFavorites}
            priority={hasFirstCardPriority && !idx}
            adTransitionSource={adTransitionSource}
            canShowViewedState={canShowViewedState}
            viewedFromStorage={viewedIds.has(String(id))}
            phoneNumberFilter={phoneNumberFilter}
          />
        );

        const { isMobileBannerRenders, isDesktopBannerRenders, isFirstBanner } =
          getBannerPosition(idx);

        if (isDesktopBannerRenders) {
          return (
            <Fragment key={`${key}_fragment`}>
              <NoSsr>
                <S.DesktopBannerBox>{inListDesktopBanner}</S.DesktopBannerBox>
              </NoSsr>
              {adCard}
            </Fragment>
          );
        }

        if (isMobileBannerRenders) {
          return (
            <Fragment key={`${key}_fragment`}>
              <NoSsr>
                <S.BannerBox>
                  {isFirstBanner
                    ? bottomBanner
                    : bannerInList?.(generateRandomNumber())}
                </S.BannerBox>
              </NoSsr>
              {adCard}
            </Fragment>
          );
        }

        return adCard;
      });
    }, [
      ads,
      hasActions,
      hasFavorite,
      onTriggerRemove,
      handleAddToFavorites,
      hasFirstCardPriority,
      t,
      getBannerPosition,
      bannerInList,
      bottomBanner,
      inListDesktopBanner,
      adTransitionSource,
      canShowViewedState,
      viewedIds,
      phoneNumberFilter,
    ]);

    if (isInitialLoading) {
      return (
        <S.Box className={className}>
          {Array.from({ length: chunkSize }, (_, index) => (
            <AdCardNew key={index} />
          ))}
        </S.Box>
      );
    }

    if (!ads?.length) return null;

    return (
      <>
        <S.Box
          {...dataStatAttr}
          data-testid={CommonTestIds.adCards.cardsBox}
          className={className}
        >
          {renderedAds}
          {isLoadingMore && <AdCard isLoading />}
          {isBannerInListAtTheEnd && !isDesktop && (
            <NoSsr>
              <S.BannerBox>
                {bannerInList?.(generateRandomNumber())}
              </S.BannerBox>
            </NoSsr>
          )}
        </S.Box>

        {isDesktop ? (
          <CollectionMasterModal
            isOpen={isCollectionMasterOpen}
            onClose={() => setIsCollectionMasterOpen(false)}
          />
        ) : (
          <CollectionMasterSheet
            isOpen={isCollectionMasterOpen}
            onClose={() => setIsCollectionMasterOpen(false)}
          />
        )}

        {hasActions && (
          <Popup
            isOpen={isPopupOpen}
            close={popupToggle}
            title={t('pages.cabinet.remove_ad_question')}
            description={t('pages.cabinet.remove_ad_popup_text')}
            isInPortal
            actions={popupActions}
          />
        )}
      </>
    );
  }
);

AdCards.displayName = 'AdCards';
